/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2024 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of subscription software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

import React, { Fragment, Suspense } from 'react';
import { getSnapshot } from 'mobx-state-tree';
import { isMobile } from 'react-device-detect';
import {
  APPSFLYERS_AUTHHOME,
  APPSFLYERS_EREADER,
  AUTHHOME_APPLICATION,
  AUTH_HOME,
  EREADER,
  EREADER_CATEGORY,
  LOCATION_IN_APP,
  NATIVE_APP_BANNER_DATA,
  NATIVE_APP_BANNER_EVENTS,
  NATIVE_APP_PROMO_TYPES,
  PISESSION_ID,
  APPSFLYERS_QRCODE_AUTHHOME,
  APPSFLYERS_QRCODE_EREADER,
  HERO_BANNER_TYPES,
  PROMO_BANNER_VARIANT,
  APPSTORE_RATING,
  PLAYSTORE_RATING
} from '../constants';
import CommonUtils from '../utils/CommonUtils';
import HeroDashboardPlugin from '../../modules/authhome/plugin/HeroDashboardPlugin';
import env from '../env';

const NativeAppBanner = CommonUtils.lazyWithPreload(() => import('@authhome/native-app-banner')
  .then(module => ({ default: module.NativeAppBanner })));
const NativePromoBanner = CommonUtils.lazyWithPreload(() => import('@authhome/native-app-banner')
  .then(module => ({ default: module.NativePromoBanner })));

/**
  * NativeAppBannerPlugin
  *
  * @author Gladson Samuel S
*/
export default class NativeAppBannerPlugin {
  constructor(context, location = AUTH_HOME) {
    this.commonContext = context;
    this.location = location;
    this.isMobileView = isMobile;
  }

  // eslint-disable-next-line class-methods-use-this
  getConfig = (variant, showDrawer) => {
    const configs = {
      // change the key names here as per LD flag name
      drawer: { showDrawer: true, showBanner: false, showModal: false },
      banner: { showDrawer: false, showBanner: true, showModal: false },
      modal: { showDrawer: false, showBanner: false, showModal: true }
    };
    // variant will be only present in new type, if not present, it will follow old flow
    return (variant && configs[variant]) || { showDrawer, showBanner: !showDrawer, showModal: false };
  };

  /**
   * Initialize native app banners
   *
   */
  initializeNativeAppBanners = () => {
    const { enableNativeAppPromoBanners } = this.commonContext?.state?.flags || {};
    const mWebBannerVariant = this.ismWebTestVariantsEnabled() ? this.getmWebVariants() : null;
    const isIntergratedMLMLaunch = CommonUtils.isIntergratedMLMLaunch();

    if (!isIntergratedMLMLaunch && enableNativeAppPromoBanners) {
      const showDrawer = this.showNativeAppDrawer();

      // if new variant and existing session mismatches with previous session, we do not show anything
      if (mWebBannerVariant && !showDrawer) {
        return;
      }
      const nativeAppBannerConfig = {
        ...this.getConfig(mWebBannerVariant, showDrawer),
        showBannerClose: true
      };

      this.commonContext.setState({ nativeAppBannerConfig });
    }
  };


  /**
   * Determines and returns whether to show Native app banner or not
   *
   */
  // eslint-disable-next-line class-methods-use-this
  showNativeAppDrawer() {
    const isGhostAccount = CommonUtils.isGhostAccount();
    const currentSessionId = window.piSession.getContextId();
    const previousSessionId = CommonUtils.getSessionStorge(PISESSION_ID);

    // Drawer will not be shown for ghost account
    if (isGhostAccount) return false;

    // If session id from session storage is same as piSessionId we will not show the banner
    if ((previousSessionId && previousSessionId) === currentSessionId) return false;

    return true;
  }

  /**
   * Closes the specified component (drawer, banner, modal) by updating the state
   * @param {string} component - The component to close ('drawer', 'banner', 'modal')
   *
   * */

  onNativePromoAfterClose = (component) => {
    this.commonContext.setState((prevState) => {
      const updatedConfig = { ...prevState.nativeAppBannerConfig };

      // Only close the specific component that triggered the close
      if (component === NATIVE_APP_PROMO_TYPES.DRAWER) {
        updatedConfig.showDrawer = false;
      } else if (component === NATIVE_APP_PROMO_TYPES.BANNER) {
        updatedConfig.showBanner = false;
      } else if (component === NATIVE_APP_PROMO_TYPES.MODAL) {
        updatedConfig.showModal = false;
      }
      return {
        ...prevState,
        nativeAppBannerConfig: updatedConfig
      };
    });
  };

  /**
   * Renders native app promotional banner or drawer
   *
   * @returns
   */
  renderMobileBanners = () => {
    const { language } = this.commonContext?.props || {};
    const { nativeAppBannerConfig } = this.commonContext?.state || {};
    const { enableNativeAppPromoBanners, mWebBannerVariant } = this.commonContext?.state?.flags || {};
    const isIntergratedMLMLaunch = CommonUtils.isIntergratedMLMLaunch();

    if (isIntergratedMLMLaunch || !enableNativeAppPromoBanners) return null;

    return (
      <Suspense fallback={<Fragment />}>
        {mWebBannerVariant
          ? (
            <NativePromoBanner
              config={nativeAppBannerConfig}
              data={this.getNativePromoBannerData()}
              locale={language.currentLanguage}
              callbacks={{
                onContinue: this.handleNativeAppDrawerContinue,
                onOpen: from => this.handleNativeAppRedirect(from),
                onClose: this.handleNativePromoBannerClose,
                onInit: this.handleNativeAppBannerInit
              }}
            />
          )
          : (
            <NativeAppBanner
              config={nativeAppBannerConfig}
              data={this.getNativeAppBannerData()}
              locale={language.currentLanguage}
              callbacks={{
                onContinue: this.handleNativeAppDrawerContinue,
                onOpen: from => this.handleNativeAppRedirect(from),
                onBannerClose: this.handleNativeAppBannerClose,
                onInit: this.handleNativeAppBannerInit
              }}
            />
          )}
      </Suspense>
    );
  };

  /**
   * course collection query param for native app AuthHome course deeplink
   *
   * @returns
   */
  courseCollectionQueryParam = () => {
    const { herobanner } = this.commonContext.props;
    const {
      bookId,
      productId,
      courseId,
      bmc
    } = HeroDashboardPlugin.activeCourseTab || {};
    const courseTypes = {
      [HERO_BANNER_TYPES.BOOK]: 'book_only',
      [HERO_BANNER_TYPES.BOOK_WITH_CHANNEL]: 'book_mapped_channel',
      [HERO_BANNER_TYPES.CHANNEL]: 'channel_only'
    };
    const courseType = courseId ? 'book_course' : courseTypes[herobanner.bannerType];
    const channelId = herobanner.channelId;

    const isAETEnabled = getSnapshot(herobanner)?.isAETEnabled || false;

    if (isAETEnabled) {
      return {
        ...(bookId ? { books: bookId } : {}),
        ...(courseId ? { courses: courseId } : {}),
        ...(productId ? { products: productId } : {})
      };
    }

    return {
      ...(courseType ? { course_type: courseType } : {}),
      ...(channelId ? { channel_id: channelId } : {}),
      ...(bookId ? { book_id: bookId } : {}),
      ...(courseId ? { course_id: courseId } : {}),
      ...(productId ? { product_id: productId } : {}),
      ...(bmc ? { bmc } : {})
    };
  };

  /**
   * Constructs and returns appsflyer query parameter
   *
   * @returns
   */
  constructQueryParam = () => {
    const APPSFLYERS_QUERY_PARMS = this.location === EREADER ? APPSFLYERS_EREADER : APPSFLYERS_AUTHHOME;
    let queryParams = {
      af_xp: APPSFLYERS_QUERY_PARMS.af_xp,
      pid: APPSFLYERS_QUERY_PARMS.pid,
      c: APPSFLYERS_QUERY_PARMS.c,
      deep_link_value: APPSFLYERS_QUERY_PARMS.deep_link_value,
      af_dp: APPSFLYERS_QUERY_PARMS.af_dp,
      af_reengagement_window: APPSFLYERS_QUERY_PARMS.af_reengagement_window,
      is_retargeting: APPSFLYERS_QUERY_PARMS.is_retargeting
    };
    const piRefreshToken = CommonUtils.getPiRefreshToken();

    if (this.location === EREADER) {
      const { pageId } = this.commonContext;
      const { course } = this.commonContext.props;
      const { bookID, id } = this.commonContext?.commonPlugin?.getProduct();

      queryParams.books = encodeURIComponent(bookID || '');
      queryParams.pages = encodeURIComponent(pageId || '');
      queryParams.products = encodeURIComponent(id || '');

      if (course) {
        queryParams.courses = encodeURIComponent(course.id || '');
      }
    } else {
      const courseCollectionQueryParam = this.courseCollectionQueryParam();
      queryParams = {
        ...queryParams,
        ...courseCollectionQueryParam
      };
    }

    if (piRefreshToken) {
      queryParams.auth_key = encodeURIComponent(piRefreshToken || '');
      queryParams.auth_env = encodeURIComponent(env.ENVIRONMENT || '');
    }

    return queryParams;
  };

  /**
   * Handles native app open button redirection
   *
   * @param {*} from
   */
  handleNativeAppRedirect = (from = NATIVE_APP_PROMO_TYPES.DRAWER) => {
    const appsflyersBaseUrl = env.APPSFLYER_BASE_URL;
    const {
      NEW_EVENT_LABEL
    } = NATIVE_APP_BANNER_EVENTS;
    const mwebTestVariantsEventLabel = this.ismWebTestVariantsEnabled()
      ? NEW_EVENT_LABEL[this.getmWebVariants()].INSTALL : null;

    this.handleNativeAppBannerClickEvents(mwebTestVariantsEventLabel || NATIVE_APP_BANNER_EVENTS.EVENT_LABEL[from]);

    const constructedQueryParams = new URLSearchParams(this.constructQueryParam());

    if (constructedQueryParams?.size > 0) {
      window.open(`${appsflyersBaseUrl}?${constructedQueryParams.toString()}`);
    }
  };


  /**
   * On click of Native app drawer continue button stores current piSession id
   *
   */
  // eslint-disable-next-line class-methods-use-this
  storeUsersContinueChoice() {
    const currentSessionId = window.piSession.getContextId();
    const previousSessionId = CommonUtils.getSessionStorge(PISESSION_ID);

    if (previousSessionId) {
      CommonUtils.clearSessionStorge(PISESSION_ID);
    }

    if (currentSessionId) {
      CommonUtils.setSessionStorge(PISESSION_ID, currentSessionId);
    }
  }

  /**
   * Handles click of native app drawer continue button
   *
   */
  handleNativeAppDrawerContinue = (component) => {
    const { mWebBannerVariant } = this.commonContext?.state?.flags || {};
    const {
      NEW_EVENT_LABEL
    } = NATIVE_APP_BANNER_EVENTS;
    const mwebTestVariantsEventLabel = this.ismWebTestVariantsEnabled()
      ? NEW_EVENT_LABEL[this.getmWebVariants()].CLOSE : null;

    this.handleNativeAppBannerClickEvents(
      mwebTestVariantsEventLabel || NATIVE_APP_BANNER_EVENTS.EVENT_LABEL.BOTTOM_DRAWER_CONTINUE
    );
    // Store sessionid in session storage and evaluate whether to show drawer or not
    this.storeUsersContinueChoice();
    if (mWebBannerVariant) {
    // Store mWeb variant choice and close component
      this.onNativePromoAfterClose(component);
      return;
    }

    this.commonContext.setState(prevState => ({
      ...prevState,
      nativeAppBannerConfig: {
        ...prevState.nativeAppBannerConfig,
        showDrawer: false,
        showBanner: true
      }
    }));
  };

  /**
   * Handles click of native app drawer close button
   *
   */
  handleNativeAppBannerClose = () => {
    this.handleNativeAppBannerClickEvents(
      NATIVE_APP_BANNER_EVENTS.EVENT_LABEL.BANNER_CLOSE
    );

    this.commonContext.setState(prevState => ({
      ...prevState,
      nativeAppBannerConfig: {
        ...prevState.nativeAppBannerConfig,
        showBanner: false
      }
    }));
  };
  /**
   * handler for close for v2 components
   * @param {*} component
   */

  handleNativePromoBannerClose = (component) => {
    const {
      NEW_EVENT_LABEL
    } = NATIVE_APP_BANNER_EVENTS;
    const mwebTestVariantsEventLabel = NEW_EVENT_LABEL[this.getmWebVariants()].CLOSE;

    this.handleNativeAppBannerClickEvents(
      mwebTestVariantsEventLabel
    );
    this.storeUsersContinueChoice();
    this.onNativePromoAfterClose(component);
  };

  /**
   * Handles Native app banner init to track view events
   *
   */
  handleNativeAppBannerInit = () => {
    setTimeout(() => {
      CommonUtils.appDomElementIntersectionTracker({
        locationInApp: this.location === AUTH_HOME ? LOCATION_IN_APP.AUTHHOME : EREADER_CATEGORY
      });
    }, 100);
  };

  /**
   * Triggers click event for all native app banner actionable elements
   *
   * @param {*} eventLabel
   */
  handleNativeAppBannerClickEvents(eventLabel) {
    const { EVENT_NAME, EVENT_ACTION } = NATIVE_APP_BANNER_EVENTS;

    CommonUtils.dispatchGaEvent(
      AUTHHOME_APPLICATION,
      EVENT_NAME,
      EVENT_ACTION,
      eventLabel,
      {
        app_activity_area_code: this.location === AUTH_HOME ? LOCATION_IN_APP.AUTHHOME : EREADER_CATEGORY,
        is_ghost_account: CommonUtils.isGhostAccount()
      }
    );
  }

  /**
   * Constructs and returns Native app banner data
   *
   * @returns
   */
  getNativeAppBannerData() {
    const {
      CTA_VARIANTS,
      IDS,
      PEARSON_ICON
    } = NATIVE_APP_BANNER_DATA;
    const { language } = this.commonContext.props;

    return {
      drawer: {
        title: language.getMessage('nativeAppBanner.drawer.title'),
        list: [{
          icon: PEARSON_ICON,
          text: language.getMessage('nativeAppBanner.drawer.pearsonApp'),
          cta: {
            id: IDS.open,
            text: language.getMessage('open'),
            variant: CTA_VARIANTS.PRIMARY
          }
        }, {
          renderBrowserIcon: true,
          cta: {
            id: IDS.continue,
            text: language.getMessage('continue'),
            variant: CTA_VARIANTS.SECONDARY
          }
        }]
      },
      banner: {
        icon: PEARSON_ICON,
        title: language.getMessage('nativeAppBanner.banner.title'),
        description: language.getMessage('nativeAppBanner.banner.description'),
        cta: {
          text: language.getMessage('open'),
          variant: CTA_VARIANTS.PRIMARY
        }
      }
    };
  }

  getNativePromoBannerData = () => {
    const { CTA_VARIANTS, IDS } = NATIVE_APP_BANNER_DATA;
    const { language } = this.commonContext.props;
    const appstoreRating = this.commonContext.getContainerData(
      'heroView',
      APPSTORE_RATING
    ) || null;
    const playstoreRating = this.commonContext.getContainerData(
      'heroView',
      PLAYSTORE_RATING
    ) || null;
    const storeRatings = [playstoreRating, appstoreRating];
    return {
      drawer: {
        source: PROMO_BANNER_VARIANT.V2,
        title: language.getMessage('nativeAppBanner.drawer.title'),
        ratings: storeRatings,
        list: [{
          text: language.getMessage('nativeAppBanner.drawer.pearsonApp'),
          description: language.getMessage('nativePromoBanner.description'),
          cta: {
            id: IDS.open,
            text: language.getMessage('open'),
            variant: CTA_VARIANTS.PRIMARY
          }
        }, {
          renderBrowserIcon: true,
          cta: {
            id: IDS.continue,
            text: language.getMessage('nativePromoBanner.continueInBrowser'),
            variant: CTA_VARIANTS.SECONDARY
          }
        }]
      },
      banner: {
        title: language.getMessage('nativeAppBanner.banner.title'),
        ratings: storeRatings,
        description: language.getMessage('nativePromoBanner.description'),
        cta: {
          text: language.getMessage('open'),
          variant: CTA_VARIANTS.PRIMARY
        }
      },
      modal: {
        source: PROMO_BANNER_VARIANT.V2,
        title: language.getMessage('nativeAppBanner.drawer.pearsonApp'),
        description: language.getMessage('nativePromoBanner.description'),
        ratings: storeRatings,
        cta: {
          id: IDS.install,
          text: language.getMessage('open'),
          variant: CTA_VARIANTS.PRIMARY
        }
      }
    };
  };


  /**
   * Constructs and returns appsflyer query parameter
   *
   * @returns
   */
  constructQRCodeQueryParam = (jwtToken, uId) => {
    const APPSFLYERS_QRCODE_QUERY_PARMS = this.location === EREADER ? APPSFLYERS_QRCODE_EREADER : APPSFLYERS_QRCODE_AUTHHOME;
    let queryParams = {
      pid: APPSFLYERS_QRCODE_QUERY_PARMS.pid,
      c: APPSFLYERS_QRCODE_QUERY_PARMS.c,
      deep_link_value: APPSFLYERS_QRCODE_QUERY_PARMS.deep_link_value,
      af_reengagement_window: APPSFLYERS_QRCODE_QUERY_PARMS.af_reengagement_window,
      is_retargeting: APPSFLYERS_QRCODE_QUERY_PARMS.is_retargeting
    };

    if (this.location === EREADER) {
      const { pageId } = this.commonContext;
      const { course } = this.commonContext.props;
      const { bookID, id } = this.commonContext?.commonPlugin?.getProduct();

      queryParams.books = encodeURIComponent(bookID || '');
      queryParams.pages = encodeURIComponent(pageId || '');
      queryParams.products = encodeURIComponent(id || '');

      if (course) {
        queryParams.courses = encodeURIComponent(course.id || '');
      }
    } else {
      const courseCollectionQueryParam = this.courseCollectionQueryParam();
      queryParams = {
        ...queryParams,
        ...courseCollectionQueryParam
      };
    }
    queryParams.uuid = encodeURIComponent(uId || '');
    // queryParams.jwt_token = encodeURIComponent(jwtToken || '');
    queryParams.auth_env = encodeURIComponent(env.ENVIRONMENT || '');
    queryParams.is_ghost_account = encodeURIComponent(CommonUtils.isGhostAccount() ? 'y' : 'n');

    return queryParams;
  };

  /**
   * Returns true if country is US
   *
   * @returns {Boolean}
   */
  isCountryUs = () => {
    const {
      user
    } = this.commonContext.props || {};
    const {
      homeCountryCode
    } = getSnapshot(user);

    return CommonUtils.localStringCompare(homeCountryCode, 'US');
  };

  /**
   * Returns true if mWeb test variants is enabled
   *
   * @returns {Boolean}
   */
  ismWebTestVariantsEnabled = () => {
    const {
      flags
    } = this.commonContext.state || {};

    return Boolean(flags?.mWebBannerVariant) && this.isCountryUs();
  };

  /**
   * Returns the current mWeb variant
   *
   * @returns {String}
   */
  getmWebVariants = () => {
    const {
      flags
    } = this.commonContext.state || {};

    return flags?.mWebBannerVariant;
  };
}
